import io from 'socket.io-client';
import { computed, reactive, ref } from '@vue/composition-api';
import useErrors from '@/compositions/useErrors';
import { pushDebugLog } from '@/helpers';

const socket = reactive({});
const messages = ref([]);
const SOCKET_CONNECT_TIMEOUT = 15000;

export default function useSocket() {
  const { networkError } = useErrors();

  function bindMessage(targetSocket, message, callback, once = false) {
    if (once) {
      targetSocket.once(message, callback);
      return;
    }

    targetSocket.on(message, callback);
  }

  function registerDisconnect() {
    socket.value.on('disconnect', (reason) => {
      networkError.value = true;
      pushDebugLog('socket', 'disconnect', { reason });
    });
    socket.value.on('connect', () => {
      networkError.value = false;
      pushDebugLog('socket', 'connect', {
        id: socket.value.id,
        transport: socket.value.io?.engine?.transport?.name,
      });
    });
  }

  function initSocket() {
    pushDebugLog('socket', 'initSocket', {
      url: process.env.VUE_APP_BACK_URL,
    });

    socket.value = io(
      process.env.VUE_APP_SOCKET_URL || process.env.VUE_APP_BACK_URL,
      {
        reconnection: true,
        transports: ['websocket', 'polling'],
      },
    );

    if (messages.value.length) {
      messages.value.forEach(({ message, callback, once }) => {
        bindMessage(socket.value, message, callback, once);
      });
      messages.value = [];
    }
    socket.value.on('reconnect_attempt', () => {
      pushDebugLog('socket', 'reconnect_attempt');
      socket.value.io.opts.transports = ['polling', 'websocket'];
    });
    socket.value.onAny((event, ...args) => {
      pushDebugLog('socket', 'incoming event', {
        event,
        args,
      });
    });
    socket.value.on('connect_error', (error) => {
      networkError.value = true;
      pushDebugLog('socket', 'connect_error', {
        message: error?.message,
        description: error?.description,
        context: error?.context,
        type: error?.type,
      });
    });
    socket.value.on('error', (error) => {
      pushDebugLog('socket', 'error', {
        message: error?.message || error,
      });
    });
    registerDisconnect();
  }

  function registerMessage(message, callback) {
    if (!socket.value) {
      messages.value.push({ message, callback, once: false });
      return;
    }
    bindMessage(socket.value, message, callback);
  }

  function registerMessageOnce(message, callback) {
    if (!socket.value) {
      messages.value.push({ message, callback, once: true });
      return;
    }

    bindMessage(socket.value, message, callback, true);
  }

  function sendEvent(event, data, callback) {
    pushDebugLog('socket', 'sendEvent', {
      event,
      connected: !!socket.value?.connected,
      id: socket.value?.id,
      transport: socket.value?.io?.engine?.transport?.name,
      payload: data,
      hasAckCallback: typeof callback === 'function',
    });

    if (typeof callback === 'function') {
      return socket.value.emit(event, data, callback);
    }

    return socket.value.emit(event, data);
  }

  function waitForConnection(timeout = SOCKET_CONNECT_TIMEOUT) {
    return new Promise((resolve, reject) => {
      if (!socket.value) {
        reject(new Error('Socket is not initialized'));
        return;
      }

      if (socket.value.connected) {
        pushDebugLog('socket', 'waitForConnection resolved immediately', {
          id: socket.value.id,
          transport: socket.value.io?.engine?.transport?.name,
        });
        resolve(socket.value);
        return;
      }

      let timeoutId = null;

      const onConnectError = (error) => {
        clearTimeout(timeoutId);
        socket.value.off('connect');
        socket.value.off('connect_error');
        reject(error || new Error('Socket connection failed'));
      };

      const onConnect = () => {
        clearTimeout(timeoutId);
        socket.value.off('connect');
        socket.value.off('connect_error');
        pushDebugLog('socket', 'waitForConnection connected', {
          id: socket.value.id,
          transport: socket.value.io?.engine?.transport?.name,
        });
        resolve(socket.value);
      };

      timeoutId = setTimeout(() => {
        socket.value.off('connect');
        socket.value.off('connect_error');
        pushDebugLog('socket', 'waitForConnection timeout', {
          timeout,
        });
        reject(new Error('Socket connection timed out'));
      }, timeout);

      socket.value.once('connect', onConnect);
      socket.value.once('connect_error', onConnectError);
      pushDebugLog('socket', 'waitForConnection pending');
    });
  }

  return {
    socket: computed(() => socket.value),
    initSocket,
    registerMessage,
    registerMessageOnce,
    sendEvent,
    waitForConnection,
    registerDisconnect,
  };
}
